import { useState, useRef, useEffect } from "react";
import { useStartProject } from "@/lib/startProjectContext";
import { CheckCircle2, Upload, X, ShieldCheck, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Lighting",
  "Furniture",
  "Home décor",
  "Textile",
  "Brass & metalware",
  "Fashion accessories",
  "Kitchen & tableware",
  "Garden & outdoor",
  "Other",
];

const TIMELINES = [
  "Under 60 days",
  "60–90 days",
  "3–4 months",
  "4–6 months",
  "6+ months",
  "Flexible",
];

/**
 * StartProjectForm — the shared two-step brief.
 * `variant="modal"` renders inside the overlay; `variant="page"` renders full-width for /start-a-project.
 * When submission succeeds, the form shows a confirmation screen.
 */
export const StartProjectForm = ({ variant = "modal", onSubmitted } = {}) => {
  const { draft, setDraft, reset, close } = useStartProject();
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    // Auto-focus first input when step mounts.
    if (firstInputRef.current) firstInputRef.current.focus();
  }, [step]);

  const set = (patch) => setDraft(patch);

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []).slice(0, 8);
    if (arr.length === 0) return;
    // Optimistic — show entries immediately with "uploading" state.
    const optimistic = arr.map((f) => ({
      name: f.name,
      size: f.size,
      type: f.type,
      uploading: true,
    }));
    set({ reference_files: [...(draft.reference_files || []), ...optimistic].slice(0, 8) });

    // Upload each file to /api/project-briefs/uploads and swap in the storage record.
    for (let i = 0; i < arr.length; i += 1) {
      const file = arr[i];
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await fetch(`${BACKEND}/api/project-briefs/uploads`, {
          method: "POST",
          body: form,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Upload failed (${res.status})`);
        }
        const rec = await res.json();
        setDraft((prev) => ({
          ...prev,
          reference_files: prev.reference_files.map((rf) =>
            rf.name === file.name && rf.uploading
              ? { name: rec.original_filename, size: rec.size, type: rec.content_type, id: rec.id, storage_path: rec.storage_path }
              : rf
          ),
        }));
      } catch (err) {
        setDraft((prev) => ({
          ...prev,
          reference_files: prev.reference_files.map((rf) =>
            rf.name === file.name && rf.uploading
              ? { name: file.name, size: file.size, type: file.type, error: err.message || "Upload failed" }
              : rf
          ),
        }));
      }
    }
  };

  const removeFile = (idx) => {
    set({ reference_files: draft.reference_files.filter((_, i) => i !== idx) });
  };

  const validateStep1 = () => {
    const e = {};
    if (!draft.category) e.category = "Select a product category.";
    if (!draft.quantity.trim()) e.quantity = "Quantity or MOQ is required.";
    if (!draft.destination.trim()) e.destination = "Destination market is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!draft.name.trim()) e.name = "Your name is required.";
    if (!draft.company.trim()) e.company = "Company name is required.";
    if (!draft.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email)) e.email = "Enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => {
    if (!validateStep1()) return;
    setStep(2);
  };
  const prevStep = () => setStep(1);

  const submit = async (evt) => {
    evt.preventDefault();
    if (!validateStep2()) return;
    setSubmitting(true);
    setErrors({});
    try {
      const res = await fetch(`${BACKEND}/api/project-briefs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category: draft.category,
          quantity: draft.quantity,
          target_price: draft.target_price || null,
          materials: draft.materials || null,
          destination: draft.destination,
          timeline: draft.timeline || null,
          reference_notes: draft.reference_notes || null,
          reference_files: draft.reference_files
            .filter((f) => !f.error && !f.uploading)
            .map((f) => ({
              id: f.id,
              name: f.name,
              size: f.size,
              type: f.type,
              storage_path: f.storage_path,
            })),
          name: draft.name,
          company: draft.company,
          email: draft.email,
          website: draft.website || null,
          phone: draft.phone || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const raw = body.detail;
        let msg;
        if (Array.isArray(raw)) {
          // FastAPI 422 returns [{loc, msg, type}, ...] — flatten to a sentence.
          msg = raw.map((d) => (d && d.msg ? d.msg : String(d))).join(", ");
        } else if (raw && typeof raw === "object") {
          msg = raw.msg || JSON.stringify(raw);
        } else {
          msg = raw;
        }
        throw new Error(msg || `Submission failed (${res.status}).`);
      }
      setDone(true);
      reset();
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setErrors({ submit: err.message || "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const pad = variant === "modal" ? "px-8 md:px-12" : "px-6 md:px-0";

  if (done) {
    return (
      <div className={`sp-form ${pad}`} data-testid="start-project-success">
        <div className="sp-success">
          <CheckCircle2 size={44} strokeWidth={1.2} className="sp-success-icon" aria-hidden />
          <h3>Brief received.</h3>
          <p>
            Thank you. Our sourcing team will review your brief and revert within one working day —
            typically with a factory shortlist and next steps for sampling.
          </p>
          {variant === "modal" ? (
            <button
              type="button"
              className="sp-btn sp-btn-primary"
              onClick={() => {
                setDone(false);
                setStep(1);
                close();
              }}
              data-testid="start-project-success-close"
            >
              Close
            </button>
          ) : (
            <a href="/case-studies" className="sp-btn sp-btn-primary" data-testid="start-project-success-cta">
              Explore case studies →
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <form className={`sp-form ${pad}`} onSubmit={submit} noValidate data-testid="start-project-form">
      {/* Progress */}
      <div className="sp-progress" data-testid="start-project-progress">
        <div className={`sp-step ${step >= 1 ? "on" : ""}`}>
          <span className="sp-step-ix">01</span>
          <span className="sp-step-label">Project</span>
        </div>
        <div className={`sp-progress-line ${step >= 2 ? "on" : ""}`} aria-hidden />
        <div className={`sp-step ${step >= 2 ? "on" : ""}`}>
          <span className="sp-step-ix">02</span>
          <span className="sp-step-label">Contact</span>
        </div>
      </div>

      {step === 1 && (
        <div className="sp-panel" data-testid="start-project-step-1">
          <div className="sp-grid">
            <label className="sp-field sp-full">
              <span className="sp-label">Product category *</span>
              <select
                ref={firstInputRef}
                className={`sp-input ${errors.category ? "err" : ""}`}
                value={draft.category}
                onChange={(e) => set({ category: e.target.value })}
                data-testid="start-project-category"
              >
                <option value="">Select a category</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {errors.category && <span className="sp-error">{errors.category}</span>}
            </label>

            <label className="sp-field">
              <span className="sp-label">Quantity / MOQ *</span>
              <input
                type="text"
                className={`sp-input ${errors.quantity ? "err" : ""}`}
                placeholder="e.g. 500 units per SKU"
                value={draft.quantity}
                onChange={(e) => set({ quantity: e.target.value })}
                data-testid="start-project-quantity"
              />
              {errors.quantity && <span className="sp-error">{errors.quantity}</span>}
            </label>

            <label className="sp-field">
              <span className="sp-label">Target FOB price</span>
              <input
                type="text"
                className="sp-input"
                placeholder="e.g. $12–15 / unit"
                value={draft.target_price}
                onChange={(e) => set({ target_price: e.target.value })}
                data-testid="start-project-target-price"
              />
            </label>

            <label className="sp-field">
              <span className="sp-label">Destination market *</span>
              <input
                type="text"
                className={`sp-input ${errors.destination ? "err" : ""}`}
                placeholder="e.g. USA · East Coast"
                value={draft.destination}
                onChange={(e) => set({ destination: e.target.value })}
                data-testid="start-project-destination"
              />
              {errors.destination && <span className="sp-error">{errors.destination}</span>}
            </label>

            <label className="sp-field">
              <span className="sp-label">Timeline</span>
              <select
                className="sp-input"
                value={draft.timeline}
                onChange={(e) => set({ timeline: e.target.value })}
                data-testid="start-project-timeline"
              >
                <option value="">Select a timeline</option>
                {TIMELINES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>

            <label className="sp-field sp-full">
              <span className="sp-label">Materials</span>
              <input
                type="text"
                className="sp-input"
                placeholder="e.g. solid oak, brass finish, hand-blown glass"
                value={draft.materials}
                onChange={(e) => set({ materials: e.target.value })}
                data-testid="start-project-materials"
              />
            </label>

            <label className="sp-field sp-full">
              <span className="sp-label">Reference images / CAD / notes</span>
              <div className="sp-upload" data-testid="start-project-upload">
                <input
                  type="file"
                  id="sp-file"
                  multiple
                  accept="image/*,.pdf,.dwg,.dxf,.step,.stp,.iges,.igs,.stl"
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <label htmlFor="sp-file" className="sp-upload-drop">
                  <Upload size={18} strokeWidth={1.4} aria-hidden />
                  <span>
                    Attach images, CAD or specification files
                    <em>PNG · JPG · PDF · DWG · STEP · STL — up to 8 files</em>
                  </span>
                </label>
                {draft.reference_files.length > 0 && (
                  <ul className="sp-file-list">
                    {draft.reference_files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className={f.error ? "err" : f.uploading ? "uploading" : "ok"}>
                        <span>
                          {f.name}
                          {f.uploading && <em> uploading…</em>}
                          {f.error && <em> — {f.error}</em>}
                          {!f.uploading && !f.error && f.id && <em> ready</em>}
                        </span>
                        <button type="button" onClick={() => removeFile(i)} aria-label={`Remove ${f.name}`}>
                          <X size={14} strokeWidth={1.5} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </label>

            <label className="sp-field sp-full">
              <span className="sp-label">Anything else</span>
              <textarea
                className="sp-input"
                rows={3}
                placeholder="Certifications required, packaging preference, seasonality, retail context…"
                value={draft.reference_notes}
                onChange={(e) => set({ reference_notes: e.target.value })}
                data-testid="start-project-notes"
              />
            </label>
          </div>

          <div className="sp-actions">
            <span className="sp-step-hint" aria-hidden>
              Step 1 of 2 · Project
            </span>
            <button
              type="button"
              className="sp-btn sp-btn-primary"
              onClick={nextStep}
              data-testid="start-project-next"
            >
              Continue →
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="sp-panel" data-testid="start-project-step-2">
          <div className="sp-grid">
            <label className="sp-field">
              <span className="sp-label">Your name *</span>
              <input
                ref={firstInputRef}
                type="text"
                className={`sp-input ${errors.name ? "err" : ""}`}
                value={draft.name}
                onChange={(e) => set({ name: e.target.value })}
                data-testid="start-project-name"
              />
              {errors.name && <span className="sp-error">{errors.name}</span>}
            </label>
            <label className="sp-field">
              <span className="sp-label">Company *</span>
              <input
                type="text"
                className={`sp-input ${errors.company ? "err" : ""}`}
                value={draft.company}
                onChange={(e) => set({ company: e.target.value })}
                data-testid="start-project-company"
              />
              {errors.company && <span className="sp-error">{errors.company}</span>}
            </label>
            <label className="sp-field">
              <span className="sp-label">Email *</span>
              <input
                type="email"
                className={`sp-input ${errors.email ? "err" : ""}`}
                value={draft.email}
                onChange={(e) => set({ email: e.target.value })}
                data-testid="start-project-email"
              />
              {errors.email && <span className="sp-error">{errors.email}</span>}
            </label>
            <label className="sp-field">
              <span className="sp-label">Website</span>
              <input
                type="url"
                className="sp-input"
                placeholder="https://…"
                value={draft.website}
                onChange={(e) => set({ website: e.target.value })}
                data-testid="start-project-website"
              />
            </label>
            <label className="sp-field sp-full">
              <span className="sp-label">Phone (optional)</span>
              <input
                type="tel"
                className="sp-input"
                value={draft.phone}
                onChange={(e) => set({ phone: e.target.value })}
                data-testid="start-project-phone"
              />
            </label>
          </div>

          {errors.submit && (
            <p className="sp-error sp-error-block" role="alert">
              {errors.submit}
            </p>
          )}

          <div className="sp-verified" data-testid="start-project-badge">
            <ShieldCheck size={16} strokeWidth={1.4} aria-hidden />
            <span>Verified in India · Direct from factory · 23 years on-floor</span>
          </div>

          <div className="sp-actions">
            <button type="button" className="sp-btn sp-btn-ghost" onClick={prevStep} data-testid="start-project-back">
              ← Back
            </button>
            <button
              type="submit"
              className="sp-btn sp-btn-primary"
              disabled={submitting}
              data-testid="start-project-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="sp-spin" aria-hidden /> Submitting…
                </>
              ) : (
                <>Submit brief →</>
              )}
            </button>
          </div>

          <p className="sp-confidentiality" data-testid="start-project-confidentiality">
            All information you share is treated as strictly confidential. Asia Sourcing operates
            under buyer-side NDAs by default and does not disclose briefs to third parties without
            written consent.
          </p>
        </div>
      )}
    </form>
  );
};

export default StartProjectForm;
